/**
 * /plate — HustlePlate / HustleConnect tier proposal sheet.
 *
 * Public, restaurant-facing pricing sheet. Customer-pitch view:
 * shows tiers, features, and the "competition value" savings —
 * no internal margin / cost-to-build info.
 *
 * Includes:
 *   • Shared PlatformHeader (converged from bespoke PlateHeader iter253)
 *   • Print-to-PDF button (window.print() with print-only CSS)
 *   • Delivery-savings calculator panel
 *   • Founding-member call-out
 *
 * iter253: Converged onto shared PlatformHeader + SuiteFooter chrome
 * so hustleplate.au visually lines up with every other module. Root
 * gets a dark:* variant so the theme toggle in the black top-bar
 * flips the page correctly. i18n is left as-is (inline strings) —
 * the GoogleTranslateBar's Google Translate widget still handles this
 * page's dynamic translation until a bulk keying pass.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Printer, Home, ArrowRight, Check, Sparkles, Truck } from "lucide-react";
import { DEMOS } from "@/lib/demos";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { BIGMENU_SECTIONS } from "@/pages/hustleplate/HustlePlateShell";
import FounderChatPill from "@/components/FounderChatPill";

const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

const TIERS = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    period: "/mo",
    blurb: "Everything most restaurants need just to be online. Free because you're already a Directory member.",
    color: "#1f7a1f",
    competitionAud: 330,
    features: [
      "Listing in HustleConnect Directory",
      "Auto-updating WP plugin (heartbeat + auto-install)",
      "OTP-verified business profile (email + SMS + landline)",
      "Homepage template + Contact page",
      "Google Drive backups (weekly)",
      "Malware scan (daily)",
      "AEO readiness check",
      "Basic Schema.org (LocalBusiness)",
      "Opaque shortlink + crawler schema page",
      "Entry-level Rewards Program",
      "Basic multi-language (auto)",
      "Phone App (basic)",
      "HustleChat (entry)",
      "Self-serve diagnostics",
      "Council food-safety log starter (Australian-specific)",
    ],
  },
  {
    key: "starter",
    name: "Starter",
    price: "$297",
    period: "/mo",
    badge: "Founding-member discount available",
    blurb: "Sell online. Take orders. Run real marketing — all in one bill.",
    color: "#b1810a",
    competitionAud: 574,
    features: [
      "Everything in Free, plus:",
      "Location pages (per suburb/region)",
      "Enhanced schema (hours, areaServed, geo)",
      "Entry-level Shopping Cart",
      "Free recipes for customers (lead-gen magnet)",
      "PDF Pro Menus (A3/A4/A5)",
      "Phone App orders / Web orders",
      "QR-code ordering",
      "Newsletter Builder (basic, branded)",
      "Twilio SMS marketing (low-volume)",
      "Resend email campaigns",
      "Allergen badges on menu items",
      "Birthday capture at order time",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    price: "$497 – $997",
    period: "/mo",
    badge: "Founding-member discount available",
    blurb: "One platform replaces 12 vendors. Marketing automation, multi-location, AI built in.",
    color: "#a64423",
    competitionAud: 2176,
    featured: true,
    features: [
      "Everything in Starter, plus:",
      "Multi-location manager",
      "Advanced schema (ReserveAction, Reviews)",
      "A/B widgets (on-site experiments)",
      "AEO auto-optimisation",
      "Auto-feed sales to accounting (Xero / MYOB / QuickBooks)",
      "AI Receptionist (starter — answers calls)",
      "Menu Maker + AI cost-per-plate calculator",
      "More menu layouts",
      "Book-a-Table reservations",
      "Uber Eats / DoorDash / Menulog integration",
      "Customer Loyalty Pool (cross-business network)",
      "Diners aggregation analytics",
      "AI-generated schema enrichment",
      "HustleChat (advanced — bookings, FAQ, lead capture)",
      "HustleMusic in-venue streaming (entry)",
      "— Marketing automation —",
      "Auto social media posts (FB / IG / Google Biz)",
      "Auto review handling (Google / Yelp / TripAdvisor)",
      "Waitlist SMS marketing (full house → text walk-aways)",
      "Birthday specials (auto SMS / email 7 days prior)",
      "Anniversary + re-engagement campaigns",
      "Group booking manager",
      "— Operations —",
      "Inventory tracking + low-stock alerts",
      "Wastage log + analytics",
      "Staff shift scheduling",
      "Tip pooling calculator",
      "Daily P&L dashboard",
      "Recipe scaling (1 → 1000 servings)",
      "— AI-native —",
      "AI menu copywriting (dish descriptions)",
      "AI dish photography (auto-stage)",
      "AI no-show prediction",
      "AI demand forecasting (prep guidance)",
      "— Compliance (AU) —",
      "Food safety log automation (council-ready PDF)",
      "ATO BAS auto-prep (via accounting feed)",
      "ACMA SMS compliance (auto don't-call enforcement)",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "$999 – $2,997",
    period: "/mo",
    blurb: "White-label, your drivers, Olympic/Council-ready in 8+ languages.",
    color: "#7a3da3",
    competitionAud: 5348,
    features: [
      "Everything in Pro, plus:",
      "— Brand & white-label —",
      "Custom domain (your branding)",
      "Advanced multi-language (curated, 8+ languages, Olympic/Council-ready)",
      "Service / Offer schema graph (full Schema.org coverage)",
      "AI Receptionist (advanced — triage, FAQ, takeaway orders)",
      "— In-house delivery —",
      "Your own drivers + driver app",
      "Live order tracking for customers",
      "Driver dispatch & route optimisation",
      "— Seasonal & occasion —",
      "Holiday menus (prep in advance, auto-apply by date)",
      "Themed templates: Easter, Valentine's, Mother's/Father's, Christmas, St Patrick's, ANZAC Day, Melbourne Cup, AFL/NRL Grand Final, Halloween, Australia Day, Lunar New Year, Eid, Diwali, Ramadan-friendly",
      "Seasonal menus (Summer / Autumn / Winter / Spring auto-rotate)",
      "Event-based dynamic pricing windows",
      "— Advanced marketing —",
      "TikTok / Instagram Reel auto-generation from dishes",
      "AI-curated press release generator",
      "VIP customer identification (top 10% flagged)",
      "Gift card sales (digital + physical)",
      "Sub-domain landing pages per campaign",
      "Cross-restaurant promo network",
      "Negative-review escalation workflow",
      "— Multi-location ops —",
      "HustleProperty listings (real estate module)",
      "HustlePlaza vendor storefront (e-commerce)",
      "HustleMusic full streaming module",
      "Multi-newsletter + per-city duplication",
      "Stripe Connect Express (you take payments direct)",
      "Autonomous AI crew (CRON workflows)",
      "— Olympic / Council ready —",
      "Multi-stadium proximity tagging",
      "Tourist QR menus (instant 8+ language switch)",
      "Council / Tourism Australia API integration",
      "Volunteer staff onboarding (event surge)",
      "Athlete-nutrition menus (certified diets)",
      "Halal / Kosher / Vegan / Gluten-Free packages",
      "Real-time capacity sharing with council",
      "Multilingual digital signage generator",
      "— Compliance & suppliers —",
      "Liquor licence renewal reminders",
      "Insurance docs vault",
      "Supplier comparison engine",
      "Invoice OCR",
      "Council inspection portal",
      "— Enterprise support —",
      "Agency-level drill-down dashboard",
      "Priority support + onboarding manager",
      "Quarterly business review with HustleConnect team",
    ],
  },
];

export default function HustlePlateSalesPage() {
  const { t, i18n } = useTranslation();
  const [deliveryOrders, setDeliveryOrders] = useState(200);
  const [aov, setAov] = useState(40);

  // Delivery savings math — Uber/DoorDash take ~28% commission; HC own-driver is 0%.
  const competitionFee = +(aov * 0.28).toFixed(2);
  const hcFee = 0;
  const perOrderSaving = competitionFee - hcFee;
  const monthlySaving = +(deliveryOrders * perOrderSaving).toFixed(0);
  const freeMealsFunded = Math.floor(monthlySaving / aov);

  useEffect(() => {
    document.title = "HustlePlate — Pricing & Features";
  }, []);

  return (
    <div
      data-testid="hustleplate-sales-page"
      className="min-h-screen bg-[#fdf9ec] text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PrintStyles />
      <PlatformHeader salesTopGap
        brand={{ name: "HustlePlate", to: "/" }}
        nav={[
          { to: "/",             label: t("plate_nav_home", { defaultValue: "Home" }) },
          { to: "/plaza",        label: t("plate_nav_plaza", { defaultValue: "HustlePlaza" }) },
          // iter322: mega-menu — the 12 backend product tiles from
          // BIGMENU_SECTIONS are now surfaced as dropdown groups on
          // PlatformHeader itself, replacing the legacy
          // <HustlePlateBigmenu/> that used to stack below.
          ...BIGMENU_SECTIONS.map((sec) => ({
            key: sec.heading.toLowerCase().replace(/\s+/g, "-"),
            label: sec.heading,
            children: sec.items.map((it) => ({ to: it.href, label: it.label })),
          })),
          { to: "https://app.hustleplaza.au/register", label: t("plate_nav_join", { defaultValue: "Onboard restaurant" }), align: "right", variant: "primary", external: true },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px" }}>
        {/* Hero */}
        <section style={heroStyle}>
          <img src={LOGO} alt="HustlePlate" style={{ height: 56, marginBottom: 16 }} />
          <h1 style={{ fontFamily: "Georgia,serif", fontSize: 44, color: "#1a1408", margin: "0 0 12px" }}>
            One platform. One bill. Built for Australian restaurants.
          </h1>
          <p style={{ fontSize: 18, color: "#5a4a1a", maxWidth: 820, margin: "0 0 22px", lineHeight: 1.55 }}>
            HustlePlate replaces 12+ separate software subscriptions with a single, AI-native platform
            tuned for Australian compliance, council reporting, and Olympic-ready multi-language operations.
            Below is exactly what you get at each level. <strong>Print this proposal</strong> to take home,
            or click any tier's Upgrade button when you're ready to start.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
            <button
              data-testid="plate-print-pdf-btn"
              onClick={() => window.print()}
              style={ctaPrimaryStyle}
            >
              <Printer size={18} /> Print proposal (PDF)
            </button>
            <a
              href="/plaza"
              data-testid="plate-back-plaza-btn"
              style={ctaSecondaryStyle}
            >
              <Home size={18} /> Back to HustlePlaza
            </a>
          </div>
          {/* iter260 (rev iter312): silent kitchen video loop moved BELOW the two CTAs
            * so the value proposition + primary actions land above the fold; the
            * motion then rewards the scroll instead of pushing the CTAs off-screen. */}
          <div style={{ position: "relative", marginTop: 6, borderRadius: 18,
                        overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.18)",
                        aspectRatio: "16/9", maxWidth: 1000 }}>
            <video
              data-testid="plate-hero-video"
              src="https://videos.pexels.com/video-files/3196289/3196289-hd_1920_1080_25fps.mp4"
              poster="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=70"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              background: "linear-gradient(180deg, transparent 40%, rgba(253,249,236,.75) 100%)",
            }} />
          </div>

          {/* iter313: Directory pre-sell callout — stress that a free
            * public listing is included for every venue, and drive
            * unclaimed operators into the register / update flow. */}
          <div style={{
            marginTop: 22, background: "#fff8e6", border: "1px solid #ecdeb7",
            borderRadius: 12, padding: "14px 18px", display: "flex",
            flexWrap: "wrap", gap: 12, alignItems: "center",
            maxWidth: 1000,
          }} data-testid="plate-directory-callout">
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px", borderRadius: 999,
              background: "#1f7a1f", color: "#fff",
              fontSize: 11, fontWeight: 800, letterSpacing: ".08em",
            }}>
              <Check size={12} /> ALL BUSINESSES ARE LISTED FREE
            </span>
            <p style={{ margin: 0, fontSize: 14, color: "#5a4a1a",
                          lineHeight: 1.5, flex: 1, minWidth: 260 }}>
              Every ABN-verified Australian venue already sits on{" "}
              <strong>HustleDirectory</strong> at no cost. If you're not
              listed yet — or your details are out of date —{" "}
              <strong>register your business or update your existing
              profile now</strong>. It takes about two minutes and you keep
              full ownership of the listing.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link to="/claim-business"
                    data-testid="plate-directory-claim"
                    style={{ ...ctaPrimaryStyle, background: "#c2410c",
                              padding: "10px 16px", fontSize: 13 }}>
                Update my listing <ArrowRight size={14} />
              </Link>
              <Link to="/add-business"
                    data-testid="plate-directory-add"
                    style={{ ...ctaSecondaryStyle, padding: "10px 16px",
                              fontSize: 13 }}>
                Register my business
              </Link>
            </div>
          </div>
        </section>

        {/* Tiers */}
        <section style={{ display: "grid", gap: 24, marginTop: 40,
                          gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}
                 data-testid="plate-tiers-grid">
          {TIERS.map(t => <TierCard key={t.key} tier={t} />)}
        </section>

        {/* iter263: dining-room image between Tiers and Delivery panel
          * so the copy-heavy funnel gets a visual breath. */}
        <img
          src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1600&q=70"
          alt="A bustling dining room in service"
          loading="lazy"
          data-testid="plate-img-diningroom"
          style={{ width: "100%", maxWidth: 1000, marginTop: 40,
                   borderRadius: 14, aspectRatio: "16/7", objectFit: "cover",
                   boxShadow: "0 12px 30px rgba(0,0,0,.12)" }}
        />

        {/* Delivery savings */}
        <section style={{ marginTop: 56, background: "#fff8e6", border: "1px solid #ecdeb7",
                          borderRadius: 14, padding: "26px 28px" }} data-testid="plate-delivery-panel">
          <h2 style={{ fontFamily: "Georgia,serif", color: "#1a1408", margin: "0 0 8px" }}>
            <Truck size={26} style={{ verticalAlign: "middle", marginRight: 8 }} />
            The delivery-fee saving alone funds the subscription
          </h2>
          <p style={{ color: "#5a4a1a", margin: "0 0 22px", maxWidth: 800 }}>
            Uber Eats and DoorDash take roughly 28% of every order. HustleConnect's own driver network
            (Enterprise) takes 0%. Punch in your numbers below.
          </p>
          <img
            src="https://images.unsplash.com/photo-1526367790999-0150786686a2?auto=format&fit=crop&w=1400&q=70"
            alt="A courier collecting orders from the kitchen pass"
            loading="lazy"
            data-testid="plate-img-delivery"
            style={{ width: "100%", maxWidth: 900, marginBottom: 22,
                     borderRadius: 12, aspectRatio: "16/6", objectFit: "cover",
                     boxShadow: "0 10px 24px rgba(0,0,0,.10)" }}
          />
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
            <NumberInput label="Delivery orders / month" value={deliveryOrders}
                         onChange={setDeliveryOrders} testid="plate-orders-input" />
            <NumberInput label="Average order value (AUD)" value={aov}
                         onChange={setAov} testid="plate-aov-input" />
            <div style={savingsBoxStyle} data-testid="plate-savings-result">
              <div style={{ fontSize: 13, color: "#5a4a1a", marginBottom: 4 }}>You save per month</div>
              <div style={{ fontSize: 36, fontWeight: 700, color: "#1f7a1f" }}>
                ${monthlySaving.toLocaleString()}
              </div>
              <div style={{ fontSize: 13, color: "#5a4a1a", marginTop: 6 }}>
                = <strong>{freeMealsFunded}</strong> free meals you could give customers each month,
                fully funded by what Uber would have taken.
              </div>
            </div>
          </div>
        </section>

        {/* What competitors can't do */}
        <section style={{ marginTop: 56 }}>
          <h2 style={{ fontFamily: "Georgia,serif", color: "#1a1408" }}>
            <Sparkles size={24} style={{ verticalAlign: "middle", marginRight: 8 }} />
            What single-feature competitors can't supply
          </h2>
          <img
            src="https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=1400&q=70"
            alt="Chefs working the pass at service"
            loading="lazy"
            data-testid="plate-img-compare"
            style={{ width: "100%", maxWidth: 900, margin: "14px 0 18px",
                     borderRadius: 12, aspectRatio: "16/6", objectFit: "cover",
                     boxShadow: "0 10px 24px rgba(0,0,0,.10)" }}
          />
          <ul style={{ color: "#5a4a1a", lineHeight: 1.7, paddingLeft: 20, maxWidth: 820 }}>
            <li><strong>One platform, one bill</strong> — vs 12 separate vendors and the integration headaches.</li>
            <li><strong>Cross-restaurant Loyalty Pool</strong> — customers earn at any HC member venue.</li>
            <li><strong>Australian compliance baked in</strong> — council food-safety logs, ATO BAS prep,
                ACMA SMS rules. No $250/mo compliance consultant needed.</li>
            <li><strong>AI-native, not bolt-on</strong> — every tool talks to one customer brain.</li>
            <li><strong>Olympic / Council-ready language stack</strong> — 8+ languages curated for Brisbane 2032.</li>
            <li><strong>Directory-driven free baseline</strong> — competitors charge $50–150/mo for what we give free.</li>
            <li><strong>Founding-member terms</strong> — locked in for life when you sign before launch.</li>
          </ul>
        </section>

        {/* Founding member callout */}
        <section style={{ marginTop: 56, background: "#1a1408", color: "#f4eccf",
                          borderRadius: 14, padding: "30px 28px" }} data-testid="plate-founding-cta">
          <h2 style={{ fontFamily: "Georgia,serif", margin: "0 0 8px", color: "#fff" }}>
            Founding-member pricing
          </h2>
          <p style={{ margin: "0 0 14px", lineHeight: 1.6, maxWidth: 820 }}>
            We're onboarding our first 120 Australian restaurants at locked-in founding-member rates —
            substantial discounts on Starter and Pro, and a Lifetime Loyalty pricing guarantee.
            Talk to your account manager to lock yours in before launch.
          </p>
          <img
            src="https://images.unsplash.com/photo-1590846406792-0adc7f938f1d?auto=format&fit=crop&w=1400&q=70"
            alt="Restaurant owners toasting after opening their venue"
            loading="lazy"
            data-testid="plate-img-founding"
            style={{ width: "100%", maxWidth: 900, margin: "0 0 20px",
                     borderRadius: 12, aspectRatio: "16/6", objectFit: "cover",
                     boxShadow: "0 10px 24px rgba(0,0,0,.28)" }}
          />
          <a href="mailto:hello@hustleplaza.au?subject=Founding%20Member%20Enquiry"
             style={{ ...ctaPrimaryStyle, background: "#b1810a", color: "#fff" }}
             data-testid="plate-founding-email-btn">
            Email for founding rates <ArrowRight size={18} />
          </a>
          <Link to="/signup/plate_restaurant"
             style={{ ...ctaPrimaryStyle, marginLeft: 12, background: "#fff", color: "#1a1408" }}
             data-testid="plate-signup-link">
            Onboard your restaurant now <ArrowRight size={18} />
          </Link>
        </section>

      </main>
      <SuiteFooter />
      {/* iter261: floating chat-with-founder pill (Telegram + email). */}
      <FounderChatPill testid="hp-main-founder-chat" />
    </div>
  );
}

function PlateHeader() {
  return (
    <header style={headerStyle} className="plate-header-no-print">
      <Link to="/plaza" data-testid="plate-header-home"
            style={{ display: "inline-flex", alignItems: "center", gap: 8,
                     color: "#fff", textDecoration: "none", fontWeight: 600 }}>
        <Home size={18} /> Home
      </Link>
      <div style={{ flex: 1, display: "flex", gap: 4, overflowX: "auto", padding: "0 12px" }}>
        {DEMOS.map(d => (
          <Link key={d.n} to={d.to}
                data-testid={`plate-header-demo-${d.n}`}
                style={navLinkStyle} title={d.title}>
            <span style={{ opacity: 0.6, marginRight: 4 }}>{d.n}.</span>
            {d.title.split("—")[0].trim()}
          </Link>
        ))}
      </div>
      <Link to="/demo" data-testid="plate-header-playbook"
            style={{ color: "#f4eccf", textDecoration: "none", fontSize: 13, opacity: 0.8 }}>
        Full playbook ↗
      </Link>
    </header>
  );
}

function TierCard({ tier }) {
  // Use the LOW end of any price range (e.g. "$497 – $997" → 497).
  const priceMatch = tier.price.match(/\$?([\d,]+)/);
  const lowPrice = priceMatch ? parseInt(priceMatch[1].replace(/,/g, ""), 10) : 0;
  const saving = tier.competitionAud - lowPrice;
  return (
    <article data-testid={`plate-tier-${tier.key}`}
             style={{
               background: "#fff",
               border: `2px solid ${tier.featured ? tier.color : "#ecdeb7"}`,
               borderRadius: 14,
               padding: 24,
               position: "relative",
               boxShadow: tier.featured ? `0 8px 30px ${tier.color}33` : "none",
               // iter313: Enterprise spans the full grid so its 3-col
               // feature grid has room to breathe (rather than being
               // squeezed into a 1/4 tile).
               ...(tier.key === "enterprise" ? { gridColumn: "1 / -1" } : {}),
             }}>
      {tier.featured && (
        <div style={{ position: "absolute", top: -12, right: 18, background: tier.color,
                      color: "#fff", padding: "4px 12px", borderRadius: 10,
                      fontSize: 11, fontWeight: 700, letterSpacing: ".5px" }}>
          MOST POPULAR
        </div>
      )}
      <div style={{ display: "inline-block", padding: "3px 10px", borderRadius: 6,
                    background: `${tier.color}22`, color: tier.color, fontSize: 11,
                    fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase" }}>
        {tier.name}
      </div>
      <div style={{ marginTop: 14 }}>
        <span style={{ fontSize: 36, fontWeight: 700, color: "#1a1408" }}>{tier.price}</span>
        <span style={{ color: "#7a6b3a", fontSize: 16 }}>{tier.period}</span>
      </div>
      {tier.badge && (
        <div style={{ fontSize: 12, color: "#b1810a", marginTop: 4, fontWeight: 600 }}>
          {tier.badge}
        </div>
      )}
      <p style={{ color: "#5a4a1a", fontSize: 14, lineHeight: 1.5, margin: "12px 0 16px" }}>
        {tier.blurb}
      </p>
      <div style={{ background: "#faf3e0", border: "1px solid #ecdeb7", borderRadius: 8,
                    padding: "10px 12px", marginBottom: 16, fontSize: 13 }}>
        <div style={{ color: "#7a6b3a" }}>Equivalent if you bought separately:</div>
        <div style={{ color: "#1a1408", fontWeight: 700, fontSize: 16 }}>
          ${tier.competitionAud.toLocaleString()}/mo
        </div>
        <div style={{ color: "#1f7a1f", fontWeight: 600, fontSize: 13, marginTop: 2 }}>
          You save ${saving.toLocaleString()}/mo
        </div>
      </div>
      <ul style={{
        listStyle: "none", padding: 0, margin: 0,
        fontSize: 13.5, lineHeight: 1.5,
        // iter313: Enterprise has ~60 features — render them in
        // three columns on wide screens so the card height stays
        // in-line with Free / Starter / Pro. Other tiers keep the
        // single-column list. `repeat(3, 1fr)` locks to 3 cols on
        // desktop; the media-driven `minmax(0, 1fr)` prevents cell
        // overflow from long feature copy.
        ...(tier.key === "enterprise" ? {
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          columnGap: 20, rowGap: 0, alignItems: "start",
        } : {}),
      }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 8, padding: "5px 0",
                               color: f.startsWith("—") ? "#b1810a" : "#1a1408",
                               fontWeight: f.startsWith("—") ? 700 : 400,
                               fontStyle: f.startsWith("Everything") ? "italic" : "normal",
                               // Section-header rows in Enterprise span
                               // the full 3-col grid so the divider is
                               // clean and copy stays readable.
                               ...(tier.key === "enterprise" && f.startsWith("—")
                                    ? { gridColumn: "1 / -1", marginTop: i === 0 ? 0 : 6 }
                                    : {}) }}>
            {!f.startsWith("—") && !f.startsWith("Everything") && (
              <Check size={14} style={{ color: tier.color, marginTop: 4, flexShrink: 0 }} />
            )}
            <span>{f.replace(/^— | —$/g, "")}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function NumberInput({ label, value, onChange, testid }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", fontSize: 13, color: "#5a4a1a" }}>
      {label}
      <input type="number" value={value} min={0}
             onChange={e => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
             data-testid={testid}
             style={{ marginTop: 4, padding: "8px 10px", border: "1px solid #ddd0a8",
                      borderRadius: 6, width: 160, fontSize: 16, fontWeight: 600 }} />
    </label>
  );
}

function PrintStyles() {
  return (
    <style>{`
      @media print {
        .plate-header-no-print, [data-testid="plate-print-pdf-btn"],
        [data-testid="plate-back-plaza-btn"], [data-testid="plate-founding-email-btn"] { display:none !important; }
        body { background: #fff !important; }
        article { break-inside: avoid; page-break-inside: avoid; }
        section { break-inside: avoid; }
      }
    `}</style>
  );
}

/* ─── Styles ────────────────────────────────────────────────────────── */
const pageStyle = { background: "#faf3e0", minHeight: "100vh",
                    fontFamily: "system-ui, -apple-system, sans-serif", color: "#1a1408" };
const headerStyle = { display: "flex", alignItems: "center", gap: 12,
                      background: "#1a1408", color: "#fff", padding: "10px 20px",
                      position: "sticky", top: 0, zIndex: 50,
                      boxShadow: "0 2px 8px rgba(0,0,0,.25)" };
const navLinkStyle = { color: "#f4eccf", textDecoration: "none", fontSize: 12,
                       padding: "6px 10px", borderRadius: 6, whiteSpace: "nowrap",
                       transition: "background .15s ease" };
const heroStyle = { background: "#fff", border: "1px solid #ecdeb7", borderRadius: 14,
                    padding: "36px 32px" };
const ctaPrimaryStyle = { display: "inline-flex", alignItems: "center", gap: 8,
                          background: "#1a1408", color: "#fff", padding: "12px 22px",
                          borderRadius: 10, border: "none", fontSize: 15, fontWeight: 600,
                          cursor: "pointer", textDecoration: "none" };
const ctaSecondaryStyle = { display: "inline-flex", alignItems: "center", gap: 8,
                            background: "transparent", color: "#1a1408", padding: "12px 22px",
                            borderRadius: 10, border: "1.5px solid #1a1408", fontSize: 15,
                            fontWeight: 600, cursor: "pointer", textDecoration: "none" };
const savingsBoxStyle = { background: "#fff", border: "1px solid #ddd0a8", borderRadius: 10,
                          padding: "14px 18px", minWidth: 220 };
